import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { setCountryRegistry, getAllCountries } from '../utils/countries';

const CACHE_KEY = 'pg_reference_filters_v1';

const EMPTY = {
  channels: [],
  currencies: [],
  countries: [],
  processors: [],
  payment_methods: [],
  statuses: [],
  transaction_types: [],
};

const ReferenceContext = createContext({ ...EMPTY, loading: false, refresh: () => {} });

export const useReference = () => useContext(ReferenceContext);

const _readCache = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) return JSON.parse(cached);
  } catch (e) {
    /* ignore */
  }
  return null;
};

/**
 * Loads all Settings-table reference lists (channels, currencies, countries,
 * processors, payment methods, statuses, transaction types) from the DB-backed
 * endpoint once authenticated. The DB is the single source of truth for every
 * filter dropdown. Hydrates the country name registry (utils/countries.js) too.
 */
export const ReferenceProvider = ({ children }) => {
  const { token, backendUrl } = useAuth();
  const [data, setData] = useState(() => {
    const cached = _readCache();
    if (cached?.countries?.length) setCountryRegistry(cached.countries);
    return cached ? { ...EMPTY, ...cached } : { ...EMPTY };
  });
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!token || !backendUrl) return;
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/reference/filters`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const payload = await res.json();
        const next = { ...EMPTY, ...payload };
        setData(next);
        if (next.countries?.length) setCountryRegistry(next.countries);
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify(next));
        } catch (e) {
          /* ignore quota */
        }
      }
    } catch (e) {
      // Keep cached data functional on transient errors.
      void getAllCountries;
    } finally {
      setLoading(false);
    }
  }, [token, backendUrl]);

  useEffect(() => {
    if (token) refresh();
  }, [token, refresh]);

  return (
    <ReferenceContext.Provider value={{ ...data, loading, refresh }}>
      {children}
    </ReferenceContext.Provider>
  );
};
